/**
 * Lot Financial Calculations
 *
 * "Meat on the Bone" and walk-away range calculations. The math itself lives
 * in @lotlingo/shared (decisionMath) so the portal, API, and extension agree;
 * this module resolves per-lot inputs (estimates, fees, overrides) and adapts
 * the legacy return shapes.
 *
 * Fee resolution priority (highest wins):
 *   1. lot.feeOverrides.* (explicit user override on the lot)
 *   2. lot.buyerPremiumPct (scraped from the listing — premium only)
 *   3. user settings (taxRate, bidderPremium, sellerCosts, fixedLotCost)
 *   4. built-in defaults
 *
 * Note: buyerPremiumCap (dollar cap on the premium) is stored and displayed
 * but not yet applied to the math; caps only bind at high hammer prices.
 *
 * @module portal/modules/lotOperations/financials
 */

import {
  computeMeat,
  computeWalkAwayRange,
  classifyBidPosition,
  resolveFees,
  type BidPosition,
  type FeeBreakdown,
  type WalkAwayRange,
} from '@lotlingo/shared';
import { Lot } from '../types';
import { getSellChannelById } from '../sellChannels';
import { parseCurrencyValue, sanitizePercentage, sanitizeCurrency } from '../formatting';

/**
 * Resolve the effective fee breakdown for a lot from user settings,
 * the scraped buyer's premium, the lot's sell channel, and per-lot overrides.
 */
export function resolveLotFees(lot: Lot | null | undefined, settings: any): FeeBreakdown {
  const userDefaults = {
    taxRatePct: sanitizePercentage(settings?.taxRate, 0) ?? 0,
    buyerPremiumPct: sanitizePercentage(settings?.bidderPremium, 15) ?? 15,
    sellerCostsPct: sanitizePercentage(settings?.sellerCosts, 14) ?? 14,
    fixedLotCost: sanitizeCurrency(settings?.fixedLotCost, 0) ?? 0,
  };

  // Scraped premium sits between user defaults and explicit per-lot overrides.
  const scrapedPremium =
    typeof lot?.buyerPremiumPct === 'number' && Number.isFinite(lot.buyerPremiumPct)
      ? lot.buyerPremiumPct
      : undefined;
  // The lot's sell channel sets selling costs the same way.
  const channel = getSellChannelById(settings, lot?.sellChannel);

  const defaults = {
    ...userDefaults,
    ...(scrapedPremium !== undefined ? { buyerPremiumPct: scrapedPremium } : {}),
    ...(channel ? { sellerCostsPct: channel.sellerCostsPct } : {}),
  };

  return resolveFees(defaults, lot?.feeOverrides ?? undefined);
}

/** Effective target margin percent for a lot (per-lot override, else user setting, else 30). */
export function resolveTargetMarginPct(lot: Lot | null | undefined, settings: any): number {
  if (typeof lot?.targetMarginPct === 'number' && Number.isFinite(lot.targetMarginPct)) {
    return lot.targetMarginPct;
  }
  return sanitizePercentage(settings?.targetMarginPct, 30) ?? 30;
}

function readEstimates(lot: Lot): { low: number | null; high: number | null } {
  return {
    low: parseCurrencyValue(lot.lowSellValue != null ? lot.lowSellValue : lot.lowSellValueRaw),
    high: parseCurrencyValue(lot.highSellValue != null ? lot.highSellValue : lot.highSellValueRaw),
  };
}

/**
 * Calculate spend amount for a lot given current bid and settings
 */
export function calculateSpendAmount(baseNextBid: number, settings: any, lot?: Lot): number {
  return computeMeat(baseNextBid, null, null, resolveLotFees(lot, settings)).spendAmount;
}

/**
 * Calculate "Meat on the Bone" for a lot
 */
export function calculateMeatOnBone(
  lot: Lot,
  settings: any
): { meatLow: number | null; meatHigh: number | null; spendAmount: number } | null {
  const baseNextBid = parseCurrencyValue(
    lot.nextRequiredBid != null ? lot.nextRequiredBid : lot.nextRequiredBidRaw
  );
  if (baseNextBid == null) {
    return null;
  }

  const { low, high } = readEstimates(lot);
  return computeMeat(baseNextBid, low, high, resolveLotFees(lot, settings));
}

/**
 * Walk-away range for a lot: the "stay under" ceiling (low estimate still
 * clears the target margin) and the "never exceed" ceiling (high estimate
 * breaks even). Independent of the current bid.
 */
export function calculateWalkAwayRange(lot: Lot, settings: any): WalkAwayRange | null {
  const { low, high } = readEstimates(lot);
  if (low == null && high == null) return null;
  return computeWalkAwayRange(
    low,
    high,
    resolveLotFees(lot, settings),
    resolveTargetMarginPct(lot, settings)
  );
}

/**
 * Classify the lot's next required bid against its walk-away range.
 * Returns null when the range or bid is unavailable.
 */
export function classifyLotBidPosition(lot: Lot, settings: any): BidPosition | null {
  const range = calculateWalkAwayRange(lot, settings);
  if (!range) return null;
  const baseNextBid = parseCurrencyValue(
    lot.nextRequiredBid != null ? lot.nextRequiredBid : lot.nextRequiredBidRaw
  );
  if (baseNextBid == null) return null;
  return classifyBidPosition(baseNextBid, range);
}

/**
 * Check if a lot is "dead meat" (negative margin on both low and high estimates)
 */
export function isDeadMeat(lot: Lot, settings: any): boolean {
  const meat = calculateMeatOnBone(lot, settings);
  if (!meat) return false;

  const lowIsNegative = meat.meatLow != null && meat.meatLow < 0;
  const highIsNegative = meat.meatHigh != null && meat.meatHigh < 0;

  return lowIsNegative && highIsNegative;
}
